import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { sanitizeSearchInput, toValidObjectIdString } from '../utils/security';
import { fetchIadeKargolari, fetchIadeKargoDetay, iadeKargoKargoTeslimAl } from '../services/iadeKargoService';
import IadeKargoDetayModal from '../components/IadeKargoDetayModal';
import TableListFooter from '../components/TableListFooter';
import { PAGE_DEFAULT_LIMIT } from '../constants/pagination';
import { normalizeSayfalama } from '../utils/pagination';

const TABS = [
  { id: 'kargo-bekliyor', label: 'Kargo Bekliyor', durum: 'KargoBekliyor' },
  { id: 'kargoda', label: 'Kargoda', durum: 'Kargoda' },
  { id: 'tamamlandi', label: 'Tamamlandı', durum: 'Tamamlandi' },
];

const formatDateTime = (dateValue) => {
  if (!dateValue) return '—';
  try {
    const date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
    if (!date || isNaN(date.getTime())) return '—';
    return date.toLocaleString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  const msg = error?.message || error?.data?.mesaj || error?.data?.message || fallback;
  return msg === 'Failed to fetch' ? 'Sunucuya bağlanılamadı' : msg;
};

const DURUM_LABEL = { KargoBekliyor: 'Kargo Bekliyor', Kargoda: 'Kargoda', Tamamlandi: 'Tamamlandı' };

const TAB_IDS = TABS.map((t) => t.id);
const LEGACY_TAB_MAP = {
  'islem-yapilmamis': 'kargo-bekliyor',
  'islem-yapilmis': 'kargoda',
  kargolanmayanlar: 'kargo-bekliyor',
  kargolananlar: 'kargoda',
};

const IadeKargolariPage = () => {
  const { user } = useAuth();
  const isAdmin = ['admin', 'mod', 'sales', 'sevkiyat'].includes(user?.rol || '');
  const [searchParams, setSearchParams] = useSearchParams();
  const urlInitRef = useRef(false);
  const tabFromUrl = searchParams.get('tab');
  const mappedTab = tabFromUrl && LEGACY_TAB_MAP[tabFromUrl] ? LEGACY_TAB_MAP[tabFromUrl] : tabFromUrl;
  const initialTab = TAB_IDS.includes(mappedTab) ? mappedTab : 'kargo-bekliyor';
  const aramaFromUrl = searchParams.get('arama') || '';

  const [iadeKargolari, setIadeKargolari] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState(aramaFromUrl);
  const [filters, setFilters] = useState({ search: aramaFromUrl, tab: initialTab });
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationMeta, setPaginationMeta] = useState({ total: 0, totalPages: 1 });
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedIadeKargo, setSelectedIadeKargo] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const sanitizedSearch = useMemo(
    () => (filters.search && filters.search.trim() ? filters.search.trim() : ''),
    [filters.search]
  );

  const tabConfig = useMemo(() => TABS.find((t) => t.id === filters.tab) || TABS[0], [filters.tab]);

  const loadIadeKargolari = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchIadeKargolari({
        durum: tabConfig.durum,
        arama: sanitizedSearch || undefined,
        sayfa: currentPage,
        limit: PAGE_DEFAULT_LIMIT,
      });
      if (response?.basarili && response?.veri) {
        setIadeKargolari(response.veri.iadeKargolari || []);
        setPaginationMeta(normalizeSayfalama(response.veri.sayfalama));
      }
    } catch {
      setIadeKargolari([]);
    } finally {
      setLoading(false);
    }
  }, [tabConfig.durum, sanitizedSearch, currentPage]);

  useEffect(() => {
    loadIadeKargolari();
  }, [loadIadeKargolari]);

  useEffect(() => {
    if (urlInitRef.current) return;
    urlInitRef.current = true;
    if (!tabFromUrl || tabFromUrl !== initialTab) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', initialTab);
        if (aramaFromUrl) next.set('arama', aramaFromUrl);
        return next;
      }, { replace: true });
    }
  }, [tabFromUrl, mappedTab, initialTab, aramaFromUrl, setSearchParams]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: searchInput }));
      setCurrentPage(1);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (searchInput.trim()) next.set('arama', searchInput.trim());
        else next.delete('arama');
        return next;
      }, { replace: true });
    }, 1000);
    return () => clearTimeout(timeoutId);
  }, [searchInput, setSearchParams]);

  const handleViewDetail = async (ik) => {
    const id = toValidObjectIdString(ik?.id ?? ik?._id);
    if (!id) {
      window.showNotification?.('error', 'Geçersiz kayıt ID');
      return;
    }
    try {
      const response = await fetchIadeKargoDetay(id);
      if (response?.basarili && response?.veri) {
        setSelectedIadeKargo(response.veri);
        setDetailModalOpen(true);
      }
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'Detay yüklenemedi'));
    }
  };

  const handleKargoTeslimAl = async () => {
    if (!selectedIadeKargo) return;
    const id = toValidObjectIdString(selectedIadeKargo?.id ?? selectedIadeKargo?._id);
    if (!id) return;
    setActionLoading(id);
    try {
      await iadeKargoKargoTeslimAl(id);
      window.showNotification?.('success', 'Tamamlandı olarak işaretlendi');
      loadIadeKargolari();
      const res = await fetchIadeKargoDetay(id);
      if (res?.basarili && res?.veri) setSelectedIadeKargo(res.veri);
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'İşlem başarısız'));
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <main className="main-content">
      <div className="content-header">
        <h1>İade Kargoları</h1>
      </div>

      <div className="toolbar" style={{ marginBottom: '16px' }}>
        <div className="search-box" style={{ flex: 1, maxWidth: '350px' }}>
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="İade kargo no veya sipariş no ile ara..."
            value={searchInput}
            onChange={(e) => setSearchInput(sanitizeSearchInput(e.target.value, { trimEdges: false }))}
            maxLength={100}
            autoComplete="off"
          />
        </div>
      </div>

      <div className="tabs" style={{ marginBottom: '16px', display: 'flex', gap: '8px' }}>
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`tab-btn ${filters.tab === tab.id ? 'active' : ''}`}
            onClick={() => {
              setFilters((prev) => ({ ...prev, tab: tab.id }));
              setCurrentPage(1);
              setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.set('tab', tab.id);
                return next;
              }, { replace: true });
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="card">
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-gray)' }}>
            Yükleniyor...
          </div>
        ) : iadeKargolari.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-gray)' }}>
            Kayıt bulunamadı
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="admins-table">
                <thead>
                  <tr>
                    <th>İade Kargo No</th>
                    <th>Sipariş No</th>
                    <th>Kaynak</th>
                    <th>Teknik Servis</th>
                    <th>Durum</th>
                    <th>Tarih</th>
                    <th className="islemler-sutunu">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {iadeKargolari.map((ik, idx) => (
                    <tr key={toValidObjectIdString(ik?.id ?? ik?._id) || ik?.iadeKargoNumarasi || `ik-${idx}`}>
                      <td style={{ fontWeight: 500 }}>{ik.iadeKargoNumarasi}</td>
                      <td>{ik.siparisNumarasi}</td>
                      <td>{ik.stoktanIade ? 'Stoktan iade' : (ik.servisKaydiNumarasi || '—')}</td>
                      <td>{ik.teknikServisAdi || '—'}</td>
                      <td>{DURUM_LABEL[ik.durum] || ik.durum || 'Kargo Bekliyor'}</td>
                      <td>{formatDateTime(ik.olusturmaTarihi)}</td>
                      <td className="islemler-sutunu">
                        <button
                          type="button"
                          className="category-action-btn edit-btn"
                          onClick={() => handleViewDetail(ik)}
                          title="Detay"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <TableListFooter
              totalRecords={paginationMeta.total}
              currentPage={currentPage}
              totalPages={paginationMeta.totalPages || 1}
              onPageChange={setCurrentPage}
              loading={loading}
              embedded
            />
          </>
        )}
      </div>

      {detailModalOpen && selectedIadeKargo && (
        <IadeKargoDetayModal
          iadeKargo={selectedIadeKargo}
          onClose={() => setDetailModalOpen(false)}
          onKargoTeslimAl={isAdmin && selectedIadeKargo.durum === 'Kargoda' ? handleKargoTeslimAl : undefined}
        />
      )}
    </main>
  );
};

export default IadeKargolariPage;
